import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, ShoppingCart, EnvelopeSimple, UserCircle, CaretDown, House, Gear, IdentificationCard, SignIn, SignOut, UserCirclePlus } from "@phosphor-icons/react";
import { useAuth } from "../../lib/auth";
import { useAuthModal } from "../AuthModal";
import { useConsultModal } from "../ConsultModal";
import { useCart } from "../../lib/useCart";
import NotificationsPanel from "../NotificationsPanel";

/** Right-hand side of the desktop header: search pill, cart, notifications,
 * profile menu (with separate authenticated / guest variants). */
export default function HeaderActions({ onOpenSearch, onOpenCart }) {
  const [openProfile, setOpenProfile] = useState(false);
  const profileRef = useRef(null);
  const { user, logout } = useAuth();
  const { open: openAuth } = useAuthModal();
  const { open: openConsult } = useConsultModal();
  const { count: cartCount } = useCart();

  useEffect(() => {
    const onDown = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setOpenProfile(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, []);

  const initials = user ? user.name.split(" ").slice(0, 2).map((p) => p[0]).join("").toUpperCase() : "";
  const isAdmin = user && (user.role === "admin" || user.role === "founder");
  const isPro = user && user.role === "professional";
  const dashboardLink = isAdmin ? "/admin" : (isPro ? "/professional/dashboard" : "/dashboard");

  return (
    <div className="hidden lg:flex items-center gap-2.5 md-header-actions">
      <button onClick={onOpenSearch} className="md-header-search" aria-label="Open search" data-testid="header-search-pill">
        <MagnifyingGlass size={14} weight="bold" />
        <span className="md-header-search-kbd"><kbd>⌘</kbd><kbd>K</kbd></span>
      </button>
      <button onClick={onOpenCart} className="md-header-square md-header-cart" aria-label="View cart" data-testid="header-cart-icon">
        <ShoppingCart size={18} weight="duotone" />
        {cartCount > 0 && <span className="md-header-icon-badge">{cartCount}</span>}
      </button>
      {user ? (
        <>
          <NotificationsPanel />
          <Link to="/dashboard" className="md-header-square" aria-label="Messages" data-testid="header-mail-icon">
            <EnvelopeSimple size={18} weight="duotone" />
          </Link>
          <div className="relative" ref={profileRef}>
            <button onClick={() => setOpenProfile((v) => !v)} className={`md-header-avatar ${openProfile ? "open" : ""}`} data-testid="header-profile-trigger" aria-label="Open profile menu">
              <span className="md-profile-avatar" aria-hidden>{initials}</span>
              <CaretDown size={12} weight="bold" className="md-profile-caret" />
            </button>
            {openProfile && (
              <div className="md-profile-menu" role="menu" data-testid="header-profile-menu">
                <div className="md-profile-menu-head">
                  <div className="md-profile-avatar md-profile-avatar-lg" aria-hidden>{initials}</div>
                  <div className="min-w-0">
                    <div className="md-profile-menu-name">{user.name}</div>
                    <div className="md-profile-menu-email" title={user.email}>{user.email}</div>
                    <span className={`md-role-pill md-role-${user.role}`}>{user.role}</span>
                  </div>
                </div>
                <div className="md-profile-menu-sep" />
                <Link to={dashboardLink} className="md-profile-menu-item" data-testid="profile-menu-dashboard">
                  <House size={16} weight="duotone" /> <span>{isAdmin ? "Admin Panel" : (isPro ? "Professional Dashboard" : "My Dashboard")}</span>
                </Link>
                <Link to={dashboardLink} className="md-profile-menu-item" data-testid="profile-menu-profile">
                  <IdentificationCard size={16} weight="duotone" /> <span>My Profile</span>
                </Link>
                <Link to="/refer" className="md-profile-menu-item" data-testid="profile-menu-refer">
                  <UserCirclePlus size={16} weight="duotone" /> <span>Refer a friend</span>
                </Link>
                {isAdmin && (
                  <Link to="/admin/settings" className="md-profile-menu-item" data-testid="profile-menu-settings">
                    <Gear size={16} weight="duotone" /> <span>Settings</span>
                  </Link>
                )}
                <div className="md-profile-menu-sep" />
                <button onClick={logout} className="md-profile-menu-item md-profile-menu-item-danger w-full text-left" data-testid="profile-menu-logout">
                  <SignOut size={16} weight="bold" /> <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="relative" ref={profileRef}>
          <button onClick={() => setOpenProfile((v) => !v)} className={`md-header-login ${openProfile ? "open" : ""}`} data-testid="header-login" aria-label="Login or sign up">
            <UserCircle size={18} weight="duotone" />
            <span>Account</span>
            <CaretDown size={11} weight="bold" className="md-profile-caret" />
          </button>
          {openProfile && (
            <div className="md-profile-menu" role="menu" data-testid="header-login-menu">
              <div className="md-profile-menu-head md-profile-menu-head-guest">
                <div className="md-profile-avatar md-profile-avatar-lg" aria-hidden><UserCircle size={22} weight="duotone" /></div>
                <div className="min-w-0">
                  <div className="md-profile-menu-name">Welcome to MindDhara</div>
                  <div className="md-profile-menu-email">Sign in to manage bookings, orders & referrals.</div>
                </div>
              </div>
              <div className="md-profile-menu-pair">
                <button onClick={() => { openAuth("login"); setOpenProfile(false); }} className="btn-primary text-sm justify-center" data-testid="login-menu-login"><SignIn size={14} weight="bold" /> Login</button>
                <button onClick={() => { openAuth("register"); setOpenProfile(false); }} className="btn-outline text-sm justify-center" data-testid="login-menu-signup"><UserCirclePlus size={14} weight="bold" /> Sign up</button>
              </div>
              <div className="md-profile-menu-sep" />
              <button onClick={() => { openAuth("register"); setOpenProfile(false); }} className="md-profile-menu-item" data-testid="login-menu-professional">
                <IdentificationCard size={16} weight="duotone" /> <span>I am a professional</span>
              </button>
              <Link to="/refer" className="md-profile-menu-item" onClick={() => setOpenProfile(false)} data-testid="login-menu-refer">
                <UserCirclePlus size={16} weight="duotone" /> <span>Refer a friend</span>
              </Link>
              <button onClick={() => { openConsult(); setOpenProfile(false); }} className="md-profile-menu-item w-full text-left" data-testid="login-menu-consultation">
                <House size={16} weight="duotone" /> <span>Free 20-min consultation</span>
              </button>
              <Link to="/contact" className="md-profile-menu-item" onClick={() => setOpenProfile(false)} data-testid="login-menu-contact">
                <Gear size={16} weight="duotone" /> <span>Get in touch</span>
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
